import { ActionContext, ActionTree } from 'vuex'
import { State } from '@/store'
import Account from '@/api/models/Account'
import ReferenceService from '@/api/services/ReferenceService'
import { RootStateMutations } from '@/store/mutations'
import AccountService from '@/api/services/AccountService'
import CampaignService from '@/api/services/CampaignService'
import Campaign from '@/api/models/Campaign'
import SettingsService from '@/api/services/SettingsService'
import Settings from '@/api/models/Settings'
import OrderStatusesDto from '@/api/dto/OrderStatusesDto'
import ReturnStatusesDto from '@/api/dto/ReturnStatusesDto'
import PaymentTypesDto from '@/api/dto/PaymentTypesDto'
import WarehousesDto from '@/api/dto/WarehousesDto'
import { setAccountId, setCampaignId, removeCampaignId } from '@/service/storage'
import CostMappingDto from '@/api/dto/CostMappingDto'
import CatalogManagementDto from '@/api/dto/CatalogManagementDto'

const referenceService = new ReferenceService()
const accountService = new AccountService()
const campaignService = new CampaignService()
const settingsService = new SettingsService()

export const actions: ActionTree<State, State> = {
    async loadBrand (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const response = await referenceService.getBrand()

        context.commit(RootStateMutations.setBrand, Object.assign({}, response))
    },
    async loadSites (context: ActionContext<State, State>) {
        const sites = await referenceService.getSites()
        context.commit(RootStateMutations.setSites, Object.assign([], Object.values(sites)))
    },
    async loadAccounts (context: ActionContext<State, State>) {
        const response = await accountService.getAccounts()
        const accounts = Object.assign([], response).filter((a: Account) => a)

        context.commit(RootStateMutations.setAccounts, accounts)
    },
    async updateAccount (context: ActionContext<State, State>, account: Account) {
        await accountService.updateAccount(account)

        context.commit(RootStateMutations.updateAccount, account)
    },
    async selectAccount (context: ActionContext<State, State>, account: Account) {
        setAccountId(account.id)
        context.commit(RootStateMutations.setAccount, account.id)
    },
    async createAccount (context: ActionContext<State, State>, account: Account) {
        const createdAccount = await accountService.createAccount(account)
        context.commit(RootStateMutations.createAccount, createdAccount)
    },
    async refreshToken (context: ActionContext<State, State>, accountId: number) {
        const response = await accountService.getRefreshTokenUrl(accountId)
        const refreshUrl = response.url
        // запрос по урлу
        if (refreshUrl.length > 0) {
            location.assign(refreshUrl)
        }
    },
    async loadCampaigns (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const response = await campaignService.getCampaigns(context.state.currentAccount)
        const campaigns = Object.assign([], response).filter((a: Campaign) => a)

        context.commit(RootStateMutations.setCampaigns, campaigns)
    },
    async selectCampaign (context: ActionContext<State, State>, campaign: Campaign) {
        setCampaignId(campaign.id)
        context.commit(RootStateMutations.setCampaign, campaign.id)
        context.commit(RootStateMutations.setCampaignReferencesNotLoad)
    },
    async createCampaign (context: ActionContext<State, State>, campaign: Campaign) {
        const newCampaign = await campaignService.createCampaign(context.state.currentAccount, campaign)

        context.commit(RootStateMutations.addCampaign, Object.assign({}, newCampaign))
    },
    async updateCampaign (context: ActionContext<State, State>, campaign: Campaign) {
        const updatedCampaign = await campaignService.updateCampaign(context.state.currentAccount, campaign)

        context.commit(RootStateMutations.updateCampaign, updatedCampaign)
    },
    async deleteCampaign (context: ActionContext<State, State>, campaignId: number) {
        await campaignService.deleteCampaign(context.state.currentAccount, campaignId)
        removeCampaignId(campaignId)
        context.commit(RootStateMutations.deleteCampaign, campaignId)
    },
    async deleteCampaignItems (context: ActionContext<State, State>, campaignId: number) {
        await campaignService.deleteCampaignItems(context.state.currentAccount, campaignId)
    },
    async getAct (context: ActionContext<State, State>, campaignId: number) {
        await campaignService.getAct(context.state.currentAccount, campaignId)
    },
    async loadSettingsBase (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const baseSettings = await settingsService.getBase(context.state.currentAccount, context.state.currentCampaign)

        context.commit(RootStateMutations.setSettings, baseSettings)
    },
    async loadReferences (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const references = await referenceService.getReferences()

        context.commit(RootStateMutations.setReferences, Object.assign({}, references))
    },
    async loadCampaignReferences (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const references = await referenceService.getCampaignReferences(context.state.currentCampaign)

        context.commit(RootStateMutations.setCampaignReferences, Object.assign({}, references))
    },
    async saveSettings (context: ActionContext<State, State>, settings: Settings) {
        const updatedBaseSettings = await settingsService.updateBase(context.state.currentAccount, context.state.currentCampaign, settings)

        context.commit(RootStateMutations.setSettings, updatedBaseSettings)
    },
    async loadPriceLink (context: ActionContext<State, State>) {
        let link = await settingsService.getPriceLink(context.state.currentAccount, context.state.currentCampaign)
        // костыль для случаев когда тело ответа пустое и response выдает пустой объект
        link = (typeof link !== 'string') ? '' : link

        context.commit(RootStateMutations.setPriceLink, link)
    },
    async loadNotificationKey (context: ActionContext<State, State>) {
        let key = await settingsService.getNotificationKey(context.state.currentAccount, context.state.currentCampaign)
        // костыль для случаев когда тело ответа пустое и response выдает пустой объект
        key = (typeof key !== 'string') ? '' : key

        context.commit(RootStateMutations.setNotificationKey, key)
    },
    async loadOrderStatusMapping (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const mapping = await settingsService.getOrderStatusMapping(context.state.currentAccount, context.state.currentCampaign)

        context.commit(RootStateMutations.setOrderStatuses, mapping)
    },
    async saveOrderStatusMapping (context: ActionContext<State, State>, mapping: OrderStatusesDto) {
        const updatedMapping = await settingsService.updateOrderStatusMapping(context.state.currentAccount, context.state.currentCampaign, mapping)

        context.commit(RootStateMutations.setOrderStatuses, updatedMapping)
    },
    async loadReturnStatusMapping (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const mapping = await settingsService.getReturnStatusMapping(context.state.currentAccount, context.state.currentCampaign)
        context.commit(RootStateMutations.setReturnOrderStatuses, mapping)
    },
    async saveReturnStatusMapping (context: ActionContext<State, State>, mapping: ReturnStatusesDto) {
        const updatedMapping = await settingsService.updateReturnStatusMapping(context.state.currentAccount, context.state.currentCampaign, mapping)
        context.commit(RootStateMutations.setReturnOrderStatuses, updatedMapping)
    },
    async loadPaymentsMapping (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const mapping = await settingsService.getPaymentsMapping(context.state.currentAccount, context.state.currentCampaign)

        context.commit(RootStateMutations.setPaymentTypes, mapping)
    },
    async savePaymentsMapping (context: ActionContext<State, State>, mapping: PaymentTypesDto) {
        const updatedMapping = await settingsService.updatePaymentsMapping(context.state.currentAccount, context.state.currentCampaign, mapping)

        context.commit(RootStateMutations.setPaymentTypes, updatedMapping)
    },
    async loadWarehousesMapping (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const mapping = await settingsService.getWarehousesMapping(context.state.currentAccount, context.state.currentCampaign)
        context.commit(RootStateMutations.setWarehousesMapping, mapping.warehouses)
    },
    async saveWarehousesMapping (context: ActionContext<State, State>, mapping: WarehousesDto) {
        const updatedMapping = await settingsService.updateWarehousesMapping(context.state.currentAccount, context.state.currentCampaign, mapping)

        context.commit(RootStateMutations.setWarehousesMapping, updatedMapping.warehouses)
    },
    async loadCostMappings (context: ActionContext<State, State>) {
        context.commit(RootStateMutations.setLoading, true)
        const mapping = await settingsService.getCosts(context.state.currentAccount, context.state.currentCampaign)
        context.commit(RootStateMutations.setCosts, mapping.costs)
    },
    async saveCostMappings (context: ActionContext<State, State>, mapping: CostMappingDto) {
        const updatedMapping = await settingsService.updateCosts(context.state.currentAccount, context.state.currentCampaign, mapping)
        context.commit(RootStateMutations.setCosts, updatedMapping.costs)
    },
    async loadCatalogManagementMapping (context: ActionContext<State, State>) {
        const updatedMapping = await settingsService.getCatalogManagementMapping(context.state.currentAccount, context.state.currentCampaign)

        context.commit(RootStateMutations.setCatalogMapping, updatedMapping)
    },
    async saveCatalogManagementMapping (context: ActionContext<State, State>, mapping: CatalogManagementDto) {
        const updatedMapping = await settingsService.updateCatalogManagementMapping(context.state.currentAccount, context.state.currentCampaign, mapping)

        context.commit(RootStateMutations.setCatalogMapping, updatedMapping)
    },
    async exportOrder (context: ActionContext<State, State>, payload: { orderId: string }) {
        const campaignId = context.state.currentCampaign
        const accountId = context.state.currentAccount

        if (!campaignId || !accountId) throw new Error('Account or campaign not selected')

        return campaignService.exportOrder(
            campaignId,
            accountId,
            { orderId: payload.orderId }
        )
    }
}
